import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, ShoppingBag, Grid3X3, BookOpen, Heart, ClipboardList, MessageCircle, LogIn, UserPlus, User as UserIcon, ShoppingCart, Sparkles, Flame, HelpCircle } from 'lucide-react';
import useAuthStore from '../store/authStore';
import useCartStore from '../store/cartStore';
import useWishlistStore from '../store/wishlistStore';

/**
 * Persistent left sidebar visible only on lg+ (≥1024px) screens.
 * Hidden on admin/login routes — those have their own layout.
 *
 * The chat trigger dispatches a global `open-support-chat` event that
 * `SupportChatWidget` listens for; the floating chat button is hidden on lg+
 * so the sidebar is the single entry point on desktop.
 */
const SidebarLink = ({ to, icon: Icon, label, active, testId, badge }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`group relative flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
      active
        ? 'bg-[#5D4E3C] text-[#F5EDE4]'
        : 'text-[#5D4E3C] hover:bg-[#FAF5EE]'
    }`}
  >
    <Icon className="h-[18px] w-[18px] flex-shrink-0" />
    <span className="truncate">{label}</span>
    {badge != null && badge > 0 && (
      <span className="ml-auto text-[10px] bg-[#C4A084] text-white px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
        {badge}
      </span>
    )}
  </Link>
);

const SidebarButton = ({ onClick, icon: Icon, label, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#5D4E3C] hover:bg-[#FAF5EE] transition-colors text-left"
  >
    <Icon className="h-[18px] w-[18px] flex-shrink-0" />
    <span className="truncate">{label}</span>
  </button>
);

const DesktopSidebar = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuthStore();
  const cartItemCount = useCartStore((s) => s.items?.reduce((sum, i) => sum + (i.quantity || 0), 0) || 0);
  const wishlistCount = useWishlistStore((s) => s.wishlistIds?.length || 0);

  // Hide on admin & auth pages — they have their own layouts
  const isAdminPath =
    location.pathname.startsWith('/admin') ||
    location.pathname === '/admin-login' ||
    location.pathname === '/vendor-dashboard' ||
    location.pathname === '/login' ||
    location.pathname === '/register';

  if (isAdminPath) return null;

  const openChat = () => {
    // Dispatch a global event — SupportChatWidget listens for it
    try {
      window.dispatchEvent(new CustomEvent('open-support-chat'));
    } catch (_e) {
      // no-op
    }
  };

  const isActive = (path) =>
    path === '/'
      ? location.pathname === '/' || location.pathname.startsWith('/products')
      : location.pathname.startsWith(path);

  return (
    <aside
      className="hidden lg:flex fixed top-0 left-0 bottom-0 w-[220px] bg-white border-r border-[#E8DCC8] z-40 flex-col"
      data-testid="desktop-sidebar"
    >
      {/* Logo / brand */}
      <Link to="/home" className="flex items-center justify-center px-4 py-5 border-b border-[#E8DCC8]">
        <img
          src="/navbar-logo.png"
          alt="Homeyoursoul"
          className="max-h-12 w-auto"
        />
      </Link>

      {/* Primary nav */}
      <nav className="flex-1 overflow-y-auto py-3 flex flex-col gap-0.5">
        <SidebarLink to="/home" icon={Home} label="Home" active={location.pathname === '/home' || location.pathname === '/'} testId="sidebar-home" />
        <SidebarLink to="/products" icon={ShoppingBag} label="Shop" active={isActive('/products')} testId="sidebar-shop" />
        <SidebarLink to="/collections" icon={Sparkles} label="Kollektionen" active={isActive('/collections')} testId="sidebar-collections" />
        <SidebarLink to="/sale" icon={Flame} label="SALE" active={isActive('/sale')} testId="sidebar-sale" />
        <SidebarLink to="/categories" icon={Grid3X3} label="Kategorien" active={isActive('/categories')} testId="sidebar-categories" />
        <SidebarLink to="/blog" icon={BookOpen} label="Blog" active={isActive('/blog')} testId="sidebar-blog" />
        <SidebarLink to="/faq" icon={HelpCircle} label="FAQ" active={isActive('/faq')} testId="sidebar-faq" />

        <div className="my-3 mx-4 border-t border-[#EFE5D7]" />

        <SidebarLink to="/cart" icon={ShoppingCart} label="Warenkorb" active={isActive('/cart')} testId="sidebar-cart" badge={cartItemCount} />
        <SidebarLink to="/wishlist" icon={Heart} label="Wunschliste" active={isActive('/wishlist')} testId="sidebar-wishlist" badge={wishlistCount} />
        <SidebarLink to="/notepad" icon={ClipboardList} label="Merkzettel" active={isActive('/notepad')} testId="sidebar-notepad" />

        <div className="my-3 mx-4 border-t border-[#EFE5D7]" />

        {isAuthenticated && user ? (
          <SidebarLink
            to="/profile"
            icon={UserIcon}
            label={user.first_name ? `Hallo, ${user.first_name}` : 'Mein Konto'}
            active={isActive('/profile') || isActive('/mein-konto')}
            testId="sidebar-account"
          />
        ) : (
          <>
            <SidebarLink to="/login" icon={LogIn} label="Anmelden" active={false} testId="sidebar-login" />
            <SidebarLink to="/register" icon={UserPlus} label="Registrieren" active={false} testId="sidebar-register" />
          </>
        )}

        <div className="my-3 mx-4 border-t border-[#EFE5D7]" />

        <SidebarButton onClick={openChat} icon={MessageCircle} label="Kundenservice" testId="sidebar-chat" />
      </nav>

      {/* Footer / micro brand line */}
      <div className="px-4 py-3 border-t border-[#EFE5D7] text-[10px] tracking-[2px] uppercase text-[#9C8C7A] text-center">
        Homeyoursoul
      </div>
    </aside>
  );
};

export default DesktopSidebar;
